import { AuthenticationService } from '@akebono/core';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

import {
  AutoLotView,
  BID_CONDITION_INTACT,
  BID_CONDITION_MOTO,
  BoundaryStatus,
  PERMISSION_IGNORE_BOUNDARY_GREEN_CORNER,
} from '../../../../../const';
import { LotBoundaryInfoService } from '../../../../../services/lot-boundary-info.service';
import { LotTypeEnum } from '../../../../graphql/service/graphql-auto-main-service';
import { GreenCornerService } from '../../../../../services/green-corner-service/green-corner.service';
import { AddGreenCornerPrice } from '../../../../graphql/service/graphql-shared-familiar-service';

@Component({
  selector: 'app-green-corner-switch',
  templateUrl: './green-corner-switch.component.html',
  styleUrls: ['./green-corner-switch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GreenCornerSwitchComponent),
      multi: true,
    },
  ],
})
export class GreenCornerSwitchComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() lot: AutoLotView | null = null;
  @Input() lotType: LotTypeEnum | null = null;
  @Input() amount: number | null = null;
  @Input() bidConditionId: number | null = null;

  greenCornerData$: Observable<AddGreenCornerPrice>;
  loading$: Observable<boolean>;

  private motoBidLimit = 0;
  private checked = false;

  isBoundaryBlocked$ = of(false);
  isDisabled = false;

  onChange: (value: boolean) => void = () => {};
  onTouch: () => void = () => {};

  constructor(
    private lotBoundaryInfoService: LotBoundaryInfoService,
    private authService: AuthenticationService,
    private greenCornerService: GreenCornerService,
  ) {}

  get isMoto(): boolean {
    return this.lotType === LotTypeEnum.Moto;
  }

  get isConditionMoto(): boolean {
    return (this.bidConditionId ?? null) === BID_CONDITION_MOTO;
  }

  get isConditionIntact(): boolean {
    return (this.bidConditionId ?? null) === BID_CONDITION_INTACT;
  }

  get isMotoAmountLimitExceeded(): boolean {
    const amount = this.amount ?? 0;
    return this.isMoto && amount > this.motoBidLimit;
  }

  get isBusinessRuleBlocked(): boolean {
    if (this.bidConditionId === null) {
      return true;
    }

    if (this.isMoto) {
      const okCondition = this.isConditionMoto;
      const okAmount = !this.isMotoAmountLimitExceeded;
      return !(okCondition && okAmount);
    }

    return !this.isConditionIntact;
  }

  ngOnInit(): void {
    this.initGreenCornerData();
    if (this.lot) {
      this.checkPermissionIsBoundaryBlocked();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lotType'] || changes['amount'] || changes['bidConditionId']) {
      this.recalculateRestrictions();
    }

    if (changes['lot']) {
      this.checkPermissionIsBoundaryBlocked();
    }
  }

  checkPermissionIsBoundaryBlocked(): void {
    if (!this.lot) {
      this.isBoundaryBlocked$ = of(false);
      return;
    }

    this.isBoundaryBlocked$ = this.authService
      .checkPermissions([PERMISSION_IGNORE_BOUNDARY_GREEN_CORNER])
      .pipe(
        map((canIgnore: boolean) => {
          if (canIgnore) {
            return false;
          }

          return (
            this.lotBoundaryInfoService.getLotBoundaryInfo(this.lot)?.status ===
            BoundaryStatus.Boundary
          );
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
  }

  initGreenCornerData(): void {
    const ref = this.greenCornerService.fetchGreenCornerPrice();
    this.loading$ = ref.loading$;
    this.greenCornerData$ = ref.data$.pipe(
      tap((data) => {
        this.motoBidLimit = data.motoBidLimit;
        this.recalculateRestrictions();
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private recalculateRestrictions(): void {
    if (this.isBusinessRuleBlocked && this.value) {
      this.value = false;
    }
  }

  get value(): boolean {
    return this.checked;
  }

  set value(value: boolean) {
    this.checked = value;
    this.onChange(value);
  }

  onSwitchChange(value: boolean): void {
    this.value = value;
    this.onTouch();
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled && this.value) {
      this.value = false;
    }
  }

  writeValue(value: boolean): void {
    this.checked = value;
  }
}
